import { defineStore } from "pinia";
import { ref } from "vue";
import TiersService from "../services/TiersService";
import { IThirdParty } from "../models/thirdParty";
import { IThirdPartyApiResponse } from "../models/api/thirdPartyApiResponse";
import { IThirdPartyForm } from "../models/forms/thirdPartyForm";
import { useParameterStore } from "./parameter.store";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";

export const useThirdPartiesStore = defineStore("thirdparties", () => {
    // State
    const datas = ref<IThirdParty[]>([]);
    const current = ref<IThirdParty>({} as IThirdParty);
    const itemForm = ref<IThirdPartyForm>({} as IThirdPartyForm);
    // Indique si datas contient l'intégralité des tiers (sans filtre de type)
    const loadedAll = ref(false);

    // Actions - Hydration
    /**
     * Hydrate ThirdParties from API response
     */
    async function hydrateThirdParties(thirdParties: IThirdPartyApiResponse[]): Promise<IThirdParty[]> {
        if (thirdParties.length === 0) return [];

        // 1. Get parameter store
        const parameterStore = useParameterStore();

        // 2. Load parameters if needed
        if (parameterStore.datas.length === 0) {
            await parameterStore.load({ page: 1, pageSize: 0 });
        }

        // 3. Create Map for O(1) lookups
        const parameterMap = new Map(parameterStore.datas.map(p => [p.id, p]));

        // 4. Hydrate all entities in one pass
        const models: IThirdParty[] = [];
        for (const thirdParty of thirdParties) {
            const model = { ...thirdParty } as unknown as IThirdParty;

            // Link thirdPartyType reference (O(1) via Map)
            if (thirdParty.thirdPartyTypeId) {
                model.thirdPartyType = parameterMap.get(thirdParty.thirdPartyTypeId) ?? model.thirdPartyType;
            }

            models.push(model);
        }

        return models;
    }

    // Actions
    /**
     * Recherche de la liste des tiers
     * @param page N° de la page
     * @param pageSize Nombre de résultats par page
     * @param typeTiers Type de tiers recherché
     * @param search Valeur recherchée
     * @returns Liste des tiers correspondant
     */
    async function loadThirdParties(
        page: number = 0,
        pageSize: number = 999,
        typeTiers: string = "",
        search: string = ""
    ) {
        const result = await TiersService.getTiers(page, pageSize, typeTiers, search);

        if (result.err) {
            datas.value = [];
            loadedAll.value = false;
            return { value: [], error: result.err };
        } else {
            datas.value = await hydrateThirdParties(result.data as IThirdPartyApiResponse[]);
            datas.value.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
            // Marquer comme complet seulement si chargé sans filtre de type ni de recherche
            loadedAll.value = (typeTiers === "" && search === "");
        }

        return { value: result, error: "" };
    }

    /**
     * Alias pour compatibilité - charge tous les tiers
     */
    async function load(filter: { page: number; pageSize: number }) {
        return await loadThirdParties(filter.page, filter.pageSize, "", "");
    }

    /**
     * Creation d'un nouveau tiers
     * @returns tiers qui a été créé
     */
    async function createTiers() {
        const result = await TiersService.createTiers(itemForm.value);

        if (!result.err) {
            const [hydratedThirdParty] = await hydrateThirdParties([result.data] as IThirdPartyApiResponse[]);
            current.value = hydratedThirdParty;
        }

        return result;
    }

    /**
     * Met à jour le Store avec le tiers à modifier
     * @param id Id du tiers à modifier
     * @returns modifyTiers
     */
    async function setCurrentTiers(id: number): Promise<IThirdParty> {
        if (id === 0) {
            current.value = {} as IThirdParty;
            itemForm.value = {} as IThirdPartyForm;
        } else {
            const result = await TiersService.getTiersById(id);

            if (result) {
                const [hydratedThirdParty] = await hydrateThirdParties([result.data] as IThirdPartyApiResponse[]);

                const index = datas.value.findIndex((a) => a.id == id);
                if (index >= 0) {
                    datas.value[index] = hydratedThirdParty;
                }

                current.value = hydratedThirdParty;
                itemForm.value = await toForm(hydratedThirdParty);
            }
        }

        return current.value;
    }

    /**
     * Permet de mettre à jour un tiers et met à jour le store
     * @returns Tiers modifié
     */
    async function updateTiersInList() {
        const result = await TiersService.updateTiers(itemForm.value);

        if (!result.err) {
            const [hydratedThirdParty] = await hydrateThirdParties([result.data] as IThirdPartyApiResponse[]);
            current.value = hydratedThirdParty;

            const index = datas.value.findIndex((a) => a.id == itemForm.value.id);
            if (index >= 0) {
                datas.value[index] = current.value;
            } else {
                datas.value.push(current.value);
            }
        }

        return result;
    }

    /**
     * Convertit un ThirdParty vers formulaire
     */
    async function toForm(thirdParty: IThirdParty): Promise<IThirdPartyForm> {
        const form = {} as IThirdPartyForm;
        Object.assign(form, thirdParty);
        form.typeTiersId = thirdParty.thirdPartyTypeId ?? 0;
        return form;
    }

    return {
        // State
        datas,
        current,
        itemForm,
        loadedAll,
        // Actions
        load,
        loadThirdParties,
        createTiers,
        setCurrentTiers,
        updateTiersInList,
        toForm,
        hydrateThirdParties
    };
});

signalRManager.connection.on("MajThirdParty", async (payload: IPayloadStore) => {
    const store = useThirdPartiesStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateThirdParties([payload.data as IThirdPartyApiResponse]);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateThirdParties([payload.data as IThirdPartyApiResponse]);
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});

export const useThirdParties = useThirdPartiesStore;
